/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { FC, Fragment } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory, useLocation } from "react-router-dom"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { TroyWebTracker } from "utils/tracking"
import { EventCategory, EventAction } from "constants/tracking"

import {
  SectionDrawer,
  CategoryDrawer,
  MenuItemDrawer,
  SortCategoriesDrawer,
  SortItemsDrawer,
  SortDepartmentsDrawer,
  SortSubDepartmentsDrawer,
  LinkModifierGroupDrawer,
  DepartmentTaggingDrawer,
  SubDepartmentTaggingDrawer,
} from "components/drawers"
import { SellingTimeEditorDrawer } from "components/drawers/SellingTimeDrawer/SellingTimeEditorDrawer"
import { MultiSelectSellingTimeDrawer } from "components/drawers/SellingTimeDrawer/MultiSelectSellingTimeDrawer"
import { MultiSelectSellingTimeDrawerV3 } from "components/drawers/SellingTimeDrawer/MultiSelectSellingTimeDrawerV3"
import { useEnablePartialMenu } from "hooks/useEnablePartialMenu"
import { SubDepartmentDrawer } from "components/drawers/SubDepartmentDrawer"
import { MenuDrawerState } from "store/duck/menu.type"

export const MenuDrawerContainer: FC = () => {
  const dispatch = useDispatch()
  const enablePartialMenu = useEnablePartialMenu()

  const history = useHistory()

  const location = useLocation()

  const {
    showSection,
    showCategory,
    showSubDepartment,
    showItem,
    showSortCategories,
    showSortItems,
    showLinkModifierGroup,
    showSortDepartments,
    showSortSubDepartments,
    showSellingTimeEditor,
    showMultiSelectSellingTime,
    sourceShowItem,
    item,
    showDepartmentTaggingDrawer,
    showSubDepartmentTaggingDrawer,
  } = useSelector((state: AppState) => state.menu.drawer)

  const {
    merchantID,
    menuGroupDetail: { menuGroupID },
    merchantGroupID,
  } = useSelector((state: AppState) => state.menu)

  const handleOnCloseItemDrawer = () => {
    const isEdit = item?.itemID
    TroyWebTracker.sendEvent({
      category:
        sourceShowItem === EventCategory.menuOverviewModifyItem
          ? EventCategory.menuOverviewModifyItem
          : EventCategory.menuItemsModifyitem,
      action: EventAction.clickExit,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        INTENT: isEdit ? "EDIT_ITEM" : "ADD_ITEM",
        ITEM_ID: item?.itemID ?? "",
      },
    })
    dispatch(
      actionCreator.menu.toggleDrawer({
        item: null,
        linkedModifierGroupsDrawerState: null,
        showItem: false,
        sourceShowItem: "",
      }),
    )

    // remove deeplink url after close drawer
    if (isEdit) {
      history.replace(
        location.pathname.substring(0, location.pathname.lastIndexOf("/")),
      )
    }
  }

  const handleOnCloseTemplate = (params: MenuDrawerState) => {
    dispatch(actionCreator.menu.toggleDrawer(params))
  }

  return (
    <Fragment>
      {showSection && (
        <SectionDrawer
          data-testid="section-drawer"
          visible={showSection}
          onClose={() =>
            dispatch(
              actionCreator.menu.toggleDrawer({
                section: null,
                showSection: false,
              }),
            )
          }
        />
      )}
      {showCategory && (
        <CategoryDrawer
          visible={showCategory}
          onClose={() =>
            handleOnCloseTemplate({ category: null, showCategory: false })
          }
        />
      )}
      <SubDepartmentDrawer
        visible={!!showSubDepartment}
        onClose={() =>
          handleOnCloseTemplate({
            subDepartment: null,
            showSubDepartment: false,
          })
        }
      />
      {showItem && (
        <MenuItemDrawer
          visible={showItem}
          onClose={() => handleOnCloseItemDrawer()}
        />
      )}
      {showSortCategories && (
        <SortCategoriesDrawer
          visible={showSortCategories}
          onClose={() => handleOnCloseTemplate({ showSortCategories: false })}
        />
      )}

      {showSortItems && (
        <SortItemsDrawer
          visible={showSortItems}
          onClose={() => handleOnCloseTemplate({ showSortItems: false })}
        />
      )}

      {showSortDepartments && (
        <SortDepartmentsDrawer
          visible={showSortDepartments}
          onClose={() => handleOnCloseTemplate({ showSortDepartments: false })}
        />
      )}

      {showSortSubDepartments && (
        <SortSubDepartmentsDrawer
          visible={showSortSubDepartments}
          onClose={() =>
            handleOnCloseTemplate({ showSortSubDepartments: false })
          }
        />
      )}

      {showLinkModifierGroup && (
        <LinkModifierGroupDrawer
          visible={showLinkModifierGroup}
          onClose={() =>
            handleOnCloseTemplate({ showLinkModifierGroup: false })
          }
        />
      )}
      {showMultiSelectSellingTime && enablePartialMenu && (
        <MultiSelectSellingTimeDrawerV3
          visible={showMultiSelectSellingTime}
          onClose={() =>
            handleOnCloseTemplate({ showMultiSelectSellingTime: false })
          }
        />
      )}
      {showMultiSelectSellingTime && !enablePartialMenu && (
        <MultiSelectSellingTimeDrawer
          visible={showMultiSelectSellingTime}
          onClose={() =>
            handleOnCloseTemplate({ showMultiSelectSellingTime: false })
          }
        />
      )}
      {showSellingTimeEditor && (
        <SellingTimeEditorDrawer
          visible={showSellingTimeEditor}
          onClose={() =>
            handleOnCloseTemplate({ showSellingTimeEditor: false })
          }
        />
      )}
      {showDepartmentTaggingDrawer && (
        <DepartmentTaggingDrawer
          visible={showDepartmentTaggingDrawer}
          onClose={() =>
            handleOnCloseTemplate({ showDepartmentTaggingDrawer: false })
          }
        />
      )}
      {showSubDepartmentTaggingDrawer && (
        <SubDepartmentTaggingDrawer
          visible={showSubDepartmentTaggingDrawer}
          onClose={() =>
            handleOnCloseTemplate({ showSubDepartmentTaggingDrawer: false })
          }
        />
      )}
    </Fragment>
  )
}
